// src/components/layout/Dashboard/Users/UserEditForm.jsx
import { useState, useEffect, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { postData } from "../../../../services/Fetchnodeservices";
import { ThemeContext } from "../../../../context/ThemeContext"; // 👈 ThemeContext Import

// CollapsibleSection component (Theme-Aware)
const CollapsibleSection = ({ title, section, expandedSections, toggleSection, theme, children }) => {
  const isExpanded = expandedSections[section];
  
  return (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "12px",
      marginBottom: "16px",
      overflow: "hidden"
    }}>
      <div
        onClick={() => toggleSection(section)}
        style={{
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          background: isExpanded ? theme.primaryLight : "transparent",
          transition: "all 0.2s"
        }}
      >
        <h3 style={{ 
          margin: 0, 
          fontSize: "15px", 
          fontWeight: 600, 
          color: theme.textPrimary
        }}>
          {title}
        </h3>
        <span style={{ 
          fontSize: "16px", 
          color: theme.textMuted,
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s"
        }}>
          ▼
        </span>
      </div>
      {isExpanded && (
        <div style={{ padding: "20px", borderTop: `1px solid ${theme.border}` }}>
          {children}
        </div>
      )}
    </div>
  );
};

const UserEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext); // 👈 Get dynamic theme
  const userData = location.state?.user;
  
  console.log("Received user data:", userData);
  
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    phone: "",
    email: "",
    password: "",
    city: "",
    state: "",
    address: "",
    wallet: "",
    vip: "No",
    status: "Active",
    referral_code: "",
    referred_by: "",
    created_at: "",
    updated_at: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    account: true,
    referral: true,
    meta: false
  });

  const getInitials = (name) => {
    if (!name || name.trim() === "") return "U";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    } else {
      const firstInitial = words[0].charAt(0);
      const secondInitial = words[1].charAt(0);
      return (firstInitial + secondInitial).toUpperCase();
    }
  };

  useEffect(() => {
    if (userData) {
      let statusValue = userData.status;
      if (userData.rawStatus === "1") statusValue = "Active";
      else if (userData.rawStatus === "0") statusValue = "Inactive";
      else if (userData.rawStatus === "2") statusValue = "Blocked";
      else statusValue = userData.status || "Active";
      
      let vipValue = userData.vip;
      if (userData.rawVip === "1") vipValue = "Yes";
      else if (userData.rawVip === "0") vipValue = "No";
      else vipValue = userData.vip === "Yes" ? "Yes" : "No";
      
      setFormData({
        id: userData.id || "",
        name: userData.name || "",
        phone: userData.contact || userData.phone || "",
        email: userData.email || "",
        password: "",
        city: userData.city || "",
        state: userData.state || "",
        address: userData.address || "",
        wallet: userData.wallet || "0",
        vip: vipValue,
        status: statusValue,
        referral_code: userData.myreferal || userData.referral_code || "",
        referred_by: userData.refer_by || userData.referred_by || "",
        created_at: userData.date || userData.created_at || "",
        updated_at: userData.updated_at || ""
      });
      
      if (userData.avatar) {
        setImagePreview(userData.avatar);
      }
    } else {
      console.warn("No user data received in location.state");
      setError("No user data received. Please go back and try again.");
    }
  }, [userData]);

  const handleSmoothChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        id: formData.id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        state: formData.state,
        address: formData.address,
        wallet: formData.wallet,
        status: formData.status === "Active" ? "1" : formData.status === "Inactive" ? "0" : "2",
        is_vip: formData.vip === "Yes" ? "1" : "0",
        referral_code: formData.referral_code,
        referred_by: formData.referred_by
      };

      if (formData.password && formData.password.trim() !== "") {
        payload.password = formData.password;
      }

      console.log("Payload being sent:", payload);

      const response = await postData("users/updateUser", payload);

      console.log("API Response:", response);

      if (response.status) {
        setSuccess("User Updated Successfully");
        setTimeout(() => {
          navigate("/Dashboard/users");
        }, 1500);
      } else {
        setError(response.message || "Update failed");
        console.error("API Error Response:", response);
      }
    } catch (err) {
      console.error("Error details:", err);
      setError("Something went wrong: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = theme.primary;
    e.target.style.boxShadow = `0 0 0 2px ${theme.primary}33`;
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '';
    e.target.style.boxShadow = '';
  };

  if (!userData && !formData.id) {
    return (
      <div style={{ minHeight: "100vh", background: theme.background, padding: "24px" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div style={{ 
            background: theme.surface, 
            border: `1px solid ${theme.border}`, 
            borderRadius: "16px", 
            padding: "32px",
            textAlign: "center"
          }}>
            <h3 style={{ color: theme.textPrimary, marginBottom: "16px" }}>
              No User Data Received
            </h3>
            <p style={{ color: theme.textMuted, marginBottom: "24px" }}>
              Please go back and select a user to edit.
            </p>
            <button
              onClick={() => navigate("/Dashboard/users")}
              style={{
                padding: "10px 20px",
                background: theme.primary,
                border: "none",
                borderRadius: "8px",
                color: "#fff",
                cursor: "pointer"
              }}
            >
              Go Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  const inputStyles = {
    width: "100%",
    padding: "10px 12px",
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: "8px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    cursor: "text"
  };

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: theme.background, 
      display: "flex",
      flexDirection: "column"
    }}>
      <style>{`
        .form-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        .form-grid-full {
          grid-column: span 2;
        }
        input, select, textarea {
          transition: all 0.2s ease;
          cursor: text;
        }
        input:focus, select:focus, textarea:focus {
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 2px ${theme.primary}1a !important;
          outline: none;
        }
        input:hover, select:hover, textarea:hover {
          border-color: ${theme.primary} !important;
        }
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .form-grid-full {
            grid-column: span 1;
          }
        }
      `}</style>
      
      {/* Fixed Header */}
      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: theme.background,
        borderBottom: `1px solid ${theme.border}`,
        padding: "16px 24px"
      }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <button 
              onClick={() => navigate(-1)} 
              style={{ 
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                cursor: "pointer", 
                color: theme.textSecondary,
                fontSize: "14px",
                padding: "8px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              ← Back
            </button>
            <h1 style={{ margin: 0, color: theme.textPrimary, fontSize: "20px", fontWeight: 600 }}>
              Edit User
            </h1>
            <span style={{ 
              fontSize: "12px", 
              color: theme.textMuted,
              background: theme.surface,
              padding: "4px 12px",
              borderRadius: "20px",
              border: `1px solid ${theme.border}`
            }}>
              ID: {formData.id}
            </span>
          </div>
          
          {/* Profile Picture */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ position: "relative" }}>
              {imagePreview ? (
                <img 
                  src={imagePreview} 
                  alt="Profile" 
                  style={{ 
                    width: "44px", 
                    height: "44px", 
                    borderRadius: "50%", 
                    objectFit: "cover",
                    border: `2px solid ${theme.border}`
                  }} 
                />
              ) : (
                <div style={{ 
                  width: "44px", 
                  height: "44px", 
                  borderRadius: "50%", 
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#fff"
                }}>
                  {getInitials(formData.name)}
                </div>
              )}
              <label style={{
                position: "absolute",
                bottom: "-2px",
                right: "-2px",
                background: theme.primary,
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: `2px solid ${theme.surface}`
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <span style={{ color: "#fff", fontSize: "10px" }}>📷</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scrollable Form Content */}
      <div className="form-scroll">
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          {error && (
            <div style={{ 
              background: `${theme.error}1a`, 
              borderLeft: `3px solid ${theme.error}`,
              padding: "12px 16px", 
              marginBottom: "20px",
              borderRadius: "8px",
              color: theme.error,
              fontSize: "13px"
            }}>
              ⚠️ {error}
            </div>
          )}
          
          {success && (
            <div style={{ 
              background: `${theme.success}1a`, 
              borderLeft: `3px solid ${theme.success}`,
              padding: "12px 16px", 
              marginBottom: "20px",
              borderRadius: "8px",
              color: theme.success,
              fontSize: "13px"
            }}>
              ✓ {success}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Personal Information Section */}
            <CollapsibleSection
              title="Personal Information"
              section="personal"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              theme={theme}
            >
              <div className="form-grid">
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Full Name <span style={{ color: theme.error }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter full name"
                    required
                    style={inputStyles}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Phone Number <span style={{ color: theme.error }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter phone number"
                    required
                    style={inputStyles}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter email address"
                    style={inputStyles}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleSmoothChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      placeholder="Leave blank to keep current"
                      style={{ ...inputStyles, paddingRight: "40px" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: theme.textMuted,
                        fontSize: "16px"
                      }}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter city"
                    style={inputStyles}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter state"
                    style={inputStyles}
                  />
                </div>
                
                <div className="form-grid-full">
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Complete Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    rows="2"
                    placeholder="Enter complete address"
                    style={{ ...inputStyles, resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>
              </div>
            </CollapsibleSection>

            {/* Account Settings Section */}
            <CollapsibleSection
              title="Account Settings"
              section="account"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              theme={theme}
            >
              <div className="form-grid">
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Wallet Balance
                  </label>
                  <input
                    type="number"
                    name="wallet"
                    value={formData.wallet}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    step="0.01"
                    placeholder="Enter wallet amount"
                    style={inputStyles}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    VIP Status
                  </label>
                  <select
                    name="vip"
                    value={formData.vip}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={inputStyles}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Account Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleSmoothChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={inputStyles}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>
              </div>
            </CollapsibleSection>

            {/* Referral Information Section */}
            <CollapsibleSection
              title="Referral Information"
              section="referral"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              theme={theme}
            >
              <div className="form-grid">
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Referral Code
                  </label>
                  <input
                    type="text"
                    name="referral_code"
                    value={formData.referral_code}
                    readOnly
                    style={{ ...inputStyles, color: theme.textSecondary, cursor: "not-allowed", opacity: 0.7, fontFamily: "monospace", letterSpacing: "1px" }}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Referred By
                  </label>
                  <input
                    type="text"
                    name="referred_by"
                    value={formData.referred_by || "—"}
                    readOnly
                    style={{ ...inputStyles, color: theme.textSecondary, cursor: "not-allowed", opacity: 0.7 }}
                  />
                </div>
              </div>
            </CollapsibleSection>

            {/* Meta Information Section */}
            <CollapsibleSection
              title="Meta Information"
              section="meta"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              theme={theme}
            >
              <div className="form-grid">
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Created At
                  </label>
                  <input
                    type="text"
                    name="created_at"
                    value={formData.created_at}
                    readOnly
                    style={{ ...inputStyles, color: theme.textSecondary, cursor: "not-allowed", opacity: 0.7, fontFamily: "monospace" }}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 500, marginBottom: "6px", color: theme.textMuted }}>
                    Updated At
                  </label>
                  <input
                    type="text"
                    name="updated_at"
                    value={formData.updated_at || "—"}
                    readOnly
                    style={{ ...inputStyles, color: theme.textSecondary, cursor: "not-allowed", opacity: 0.7, fontFamily: "monospace" }}
                  />
                </div>
              </div>
            </CollapsibleSection>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "12px",
                background: loading ? theme.textMuted : theme.primary,
                border: "none",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
                marginTop: "16px",
                transition: "all 0.2s ease"
              }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEdit;